import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Persona } from "@/api/personas";
import { deleteContent } from "@/api/longform";
import { useToast } from "@/hooks/use-toast";
import {
  ExternalLink,
  Edit,
  Globe,
  Share2,
  Clipboard,
  Check,
  LoaderCircle,
  Trash2,
  Wand2,
  ChevronDown,
  BookOpen,
} from "lucide-react";
import { TwitterAvatar } from "@/components/twitter/TwitterAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "react-router-dom";

interface ContentCardProps {
  content: {
    content_id: string;
    title: string;
    content: string;
    preview: string;
    word_count: number;
    format: string;
    status: "draft" | "published" | "deleted";
    created_at: string;
    updated_at?: string;
    media_type?: string;
    media_url?: string;
    publish_info?: {
      platforms: string[];
      urls: Record<string, string>;
    };
    actual_char_count?: number;
    metadata?: {
      actual_char_count?: number;
      status?: string;
      format?: string;
      [key: string]: unknown;
    };
  };
  personaData?: Persona;
  onPublish?: (contentId: string) => void;
  onEdit?: (contentId: string) => void;
  onAiEdit?: (contentId: string) => void;
  onStatusChange?: (
    contentId: string,
    newStatus: "draft" | "published",
  ) => Promise<void>;
  onDelete?: (contentId: string) => void;
}

const LongFormContentCard = ({
  content,
  personaData,
  onPublish,
  onEdit,
  onAiEdit,
  onStatusChange,
  onDelete,
}: ContentCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isStatusChanging, setIsStatusChanging] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { toast } = useToast();

  // Skip rendering if content is deleted
  if (content.status === "deleted") {
    return null;
  }

  // Calculate word count from char count if available
  const calculateWordCount = () => {
    if (content.actual_char_count) {
      // Average English word is ~5 characters + space, so divide by ~6
      return Math.round(content.actual_char_count / 6);
    }

    // If metadata has actual_char_count, use that
    if (content.metadata?.actual_char_count) {
      return Math.round(content.metadata.actual_char_count / 6);
    }

    // Fallback to word_count if provided and not zero
    if (content.word_count && content.word_count > 0) {
      return content.word_count;
    }

    // If we have content or preview, calculate based on length
    if (content.content) {
      const charCount = content.content.length;
      return Math.round(charCount / 6);
    }

    if (content.preview) {
      const charCount = content.preview.length;
      return Math.round(charCount / 6);
    }

    // Final fallback
    return 0;
  };

  const wordCount = calculateWordCount();

  // Format the preview to show more content
  // Truncate preview at a reasonable length if needed
  const displayPreview =
    content.preview?.length > 300
      ? content.preview.substring(0, 300) + "..."
      : content.preview;

  const formatDate = (dateString: string) => {
    if (!dateString) return "Unknown date";
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      console.log(error);
      return dateString;
    }
  };

  const copyToClipboard = async () => {
    const textToCopy = content.content;
    if (!textToCopy) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleDeleteContent = async () => {
    try {
      setIsDeleting(true);
      await deleteContent(content.content_id);
      toast({ description: "Content deleted successfully" });

      // Call the parent's onDelete callback
      if (onDelete) {
        onDelete(content.content_id);
      }
    } catch (error) {
      console.error("Error deleting content:", error);
      toast({
        description: "Error deleting content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleStatusChange = async (
    e: React.MouseEvent,
    newStatus: "draft" | "published",
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (onStatusChange) {
      try {
        setIsStatusChanging(true);
        await onStatusChange(content.content_id, newStatus);
        toast({
          description: `Content is now ${newStatus === "published" ? "published" : "in draft"}`,
        });
      } catch (error) {
        console.error(`Error changing status to ${newStatus}:`, error);
        toast({
          description: `Error changing status to ${newStatus}. Please try again.`,
          variant: "destructive",
        });
      } finally {
        setIsStatusChanging(false);
      }
    }
  };

  return (
    <div className="bg-card border rounded-lg shadow-sm p-5 transition-all hover:shadow-md">
      {/* Header with avatar and meta info */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center">
          {personaData && <TwitterAvatar persona={personaData} />}
        </div>

        <div className="flex flex-col gap-2 items-end">
          <div className="flex gap-2 items-center">
            <Badge
              variant={content.status === "published" ? "posted" : "draft"}
              className="capitalize"
            >
              {content.status === "published" ? "Published" : "Draft"}
            </Badge>

            {/* Status change dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                {content.status !== "published" ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={isStatusChanging}
                  >
                    {isStatusChanging ? (
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                ) : undefined}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => handleStatusChange(e, "published")}
                  disabled={content.status === "published" || isStatusChanging}
                >
                  <Globe className="h-4 w-4 mr-2" />
                  Mark as Published
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="text-xs text-muted-foreground">
            {wordCount} words • {content.format.replace("_", " ")} •{" "}
            {formatDate(content.created_at)}
          </div>
        </div>
      </div>
      <h4 className="text-base font-semibold">{content.title}</h4>

      {/* Media source info if available */}
      {content.media_url && (
        <div className="flex items-center mb-3 text-sm text-muted-foreground">
          <span className="capitalize">{content.media_type}: </span>
          <a
            href={content.media_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline ml-1 flex items-center"
          >
            View Source <ExternalLink className="h-3 w-3 ml-1" />
          </a>
        </div>
      )}

      {/* Content preview */}
      <div className="prose prose-sm max-w-none mt-4 mb-3 whitespace-pre-line">
        {displayPreview}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 justify-between mt-4 pt-3 border-t">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(true)}
          className="flex gap-1 items-center text-primary hover:text-white"
        >
          <BookOpen className="h-4 w-4 mr-2" />
          Read full content
        </Button>

        <div className="flex flex-wrap gap-2">
          {content.status === "draft" && onPublish && (
            <Button size="sm" onClick={() => onPublish(content.content_id)}>
              <Globe className="h-4 w-4 mr-2" />
              Publish
            </Button>
          )}

          {content.status === "draft" && (
            <>
              {onAiEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                  onClick={() => onAiEdit(content.content_id)}
                >
                  <Wand2 className="h-4 w-4 mr-2" />
                  AI Edit
                </Button>
              )}

              {onEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(content.content_id)}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Delete
              </Button>
            </>
          )}

          {content.status === "published" && content.publish_info?.urls && (
            <Link
              to={Object.values(content.publish_info.urls)[0]}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="sm">
                <Globe className="h-4 w-4 mr-2" />
                View Published
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Full content dialog */}
      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500 scrollbar-track-transparent">
          <DialogHeader>
            <DialogTitle>{content.title}</DialogTitle>
            <div className="flex justify-between items-center mt-2">
              <div className="text-sm text-muted-foreground">
                {wordCount} words • {content.format.replace("_", " ")} •{" "}
                {formatDate(content.created_at)}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyToClipboard}>
                  {isCopied ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Clipboard className="h-4 w-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
                <Button variant="outline" size="sm">
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </DialogHeader>

          <div className="prose prose-sm md:prose-base max-w-none mt-4 whitespace-pre-line">
            {content.content}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Content</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              Are you sure you want to delete this content? This action cannot
              be undone.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteContent}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <LoaderCircle className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LongFormContentCard;
